import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

const TIME_UNITS = {
  MONTHS: 'months',
  DAYS: 'days',
  HOURS: 'hours',
  MINUTES: 'minutes',
};

const TIMING_DIRECTION = {
  AFTER: 'after',
  BEFORE: 'before',
};

const TIMING_REFERENCE = {
  WORKFLOW_START: 'workflow_start',
  PREVIOUS_TASK: 'previous_task',
  WORKFLOW_END: 'workflow_end',
};

const BUSINESS_DAY_OPTIONS = {
  SHIFT_NEXT: 'shift_next',
  ALL_DAYS: 'all_days',
};

export function DueDateModal({ open, onOpenChange, task, onSaveDueDate }) {
  const [months, setMonths] = useState(0);
  const [days, setDays] = useState(0);
  const [hours, setHours] = useState(0);
  const [minutes, setMinutes] = useState(0);
  const [timingDirection, setTimingDirection] = useState(TIMING_DIRECTION.AFTER);
  const [timingReference, setTimingReference] = useState(TIMING_REFERENCE.WORKFLOW_START);
  const [businessDayHandling, setBusinessDayHandling] = useState(BUSINESS_DAY_OPTIONS.SHIFT_NEXT);

  // Load existing due date configuration when modal opens
  useEffect(() => {
    if (open && task?.dueDate) {
      setMonths(task.dueDate.months || 0);
      setDays(task.dueDate.days || 0);
      setHours(task.dueDate.hours || 0);
      setMinutes(task.dueDate.minutes || 0);
      setTimingDirection(task.dueDate.timingDirection || TIMING_DIRECTION.AFTER);
      setTimingReference(task.dueDate.timingReference || TIMING_REFERENCE.WORKFLOW_START);
      setBusinessDayHandling(task.dueDate.businessDayHandling || BUSINESS_DAY_OPTIONS.SHIFT_NEXT);
    } else if (open && !task?.dueDate) {
      // Reset to defaults
      setMonths(0);
      setDays(0);
      setHours(0);
      setMinutes(0);
      setTimingDirection(TIMING_DIRECTION.AFTER);
      setTimingReference(TIMING_REFERENCE.WORKFLOW_START);
      setBusinessDayHandling(BUSINESS_DAY_OPTIONS.SHIFT_NEXT);
    }
  }, [open, task]);

  const handleSave = () => {
    const dueDateConfig = {
      months,
      days,
      hours,
      minutes,
      timingDirection,
      timingReference,
      businessDayHandling,
    };
    onSaveDueDate(dueDateConfig);
    onOpenChange(false);
  };

  const handleRemove = () => {
    onSaveDueDate(null);
    onOpenChange(false);
  };


  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Dynamic due date</DialogTitle>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Time Duration Inputs */}
          <div>
            <div className="text-sm font-medium mb-3">This task will be due:</div>
            <div className="grid grid-cols-4 gap-3">
              {/* Months */}
              <div>
                <div className="relative">
                  <Input
                    type="number"
                    min="0"
                    value={months}
                    onChange={(e) => setMonths(Math.max(0, parseInt(e.target.value) || 0))}
                    className="text-center pr-6 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                  />
                  <div className="absolute right-2 top-0 bottom-0 flex flex-col items-center justify-center pointer-events-none">
                    <button
                      type="button"
                      className="h-3 w-3 flex items-center justify-center text-xs text-muted-foreground hover:text-foreground pointer-events-auto"
                      onClick={() => setMonths(months + 1)}
                    >
                      ▲
                    </button>
                    <button
                      type="button"
                      className="h-3 w-3 flex items-center justify-center text-xs text-muted-foreground hover:text-foreground pointer-events-auto"
                      onClick={() => setMonths(Math.max(0, months - 1))}
                    >
                      ▼
                    </button>
                  </div>
                </div>
                <div className="text-xs text-center mt-1 text-muted-foreground">months</div>
              </div>

              {/* Days */}
              <div>
                <div className="relative">
                  <Input
                    type="number"
                    min="0"
                    value={days}
                    onChange={(e) => setDays(Math.max(0, parseInt(e.target.value) || 0))}
                    className="text-center pr-6 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                  />
                  <div className="absolute right-2 top-0 bottom-0 flex flex-col items-center justify-center pointer-events-none">
                    <button
                      type="button"
                      className="h-3 w-3 flex items-center justify-center text-xs text-muted-foreground hover:text-foreground pointer-events-auto"
                      onClick={() => setDays(days + 1)}
                    >
                      ▲
                    </button>
                    <button
                      type="button"
                      className="h-3 w-3 flex items-center justify-center text-xs text-muted-foreground hover:text-foreground pointer-events-auto"
                      onClick={() => setDays(Math.max(0, days - 1))}
                    >
                      ▼
                    </button>
                  </div>
                </div>
                <div className="text-xs text-center mt-1 text-muted-foreground">days</div>
              </div>

              {/* Hours */}
              <div>
                <div className="relative">
                  <Input
                    type="number"
                    min="0"
                    value={hours}
                    onChange={(e) => setHours(Math.max(0, parseInt(e.target.value) || 0))}
                    className="text-center pr-6 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                  />
                  <div className="absolute right-2 top-0 bottom-0 flex flex-col items-center justify-center pointer-events-none">
                    <button
                      type="button"
                      className="h-3 w-3 flex items-center justify-center text-xs text-muted-foreground hover:text-foreground pointer-events-auto"
                      onClick={() => setHours(hours + 1)}
                    >
                      ▲
                    </button>
                    <button
                      type="button"
                      className="h-3 w-3 flex items-center justify-center text-xs text-muted-foreground hover:text-foreground pointer-events-auto"
                      onClick={() => setHours(Math.max(0, hours - 1))}
                    >
                      ▼
                    </button>
                  </div>
                </div>
                <div className="text-xs text-center mt-1 text-muted-foreground">hours</div>
              </div>

              {/* Minutes */}
              <div>
                <div className="relative">
                  <Input
                    type="number"
                    min="0"
                    value={minutes}
                    onChange={(e) => setMinutes(Math.max(0, parseInt(e.target.value) || 0))}
                    className="text-center pr-6 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                  />
                  <div className="absolute right-2 top-0 bottom-0 flex flex-col items-center justify-center pointer-events-none">
                    <button
                      type="button"
                      className="h-3 w-3 flex items-center justify-center text-xs text-muted-foreground hover:text-foreground pointer-events-auto"
                      onClick={() => setMinutes(minutes + 1)}
                    >
                      ▲
                    </button>
                    <button
                      type="button"
                      className="h-3 w-3 flex items-center justify-center text-xs text-muted-foreground hover:text-foreground pointer-events-auto"
                      onClick={() => setMinutes(Math.max(0, minutes - 1))}
                    >
                      ▼
                    </button>
                  </div>
                </div>
                <div className="text-xs text-center mt-1 text-muted-foreground">minutes</div>
              </div>
            </div>
          </div>

          {/* Timing Selection - Two Dropdowns */}
          <div>
            <div className="text-sm font-medium mb-2">Timing</div>
            <div className="flex gap-3">
              {/* Before/After Dropdown */}
              <Select value={timingDirection} onValueChange={setTimingDirection}>
                <SelectTrigger className="flex-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={TIMING_DIRECTION.AFTER}>after</SelectItem>
                  <SelectItem value={TIMING_DIRECTION.BEFORE}>before</SelectItem>
                </SelectContent>
              </Select>

              {/* Reference Point Dropdown */}
              <Select value={timingReference} onValueChange={setTimingReference}>
                <SelectTrigger className="flex-[2]">
                  <SelectValue placeholder="Choose rule..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={TIMING_REFERENCE.WORKFLOW_START}>
                    workflow starts
                  </SelectItem>
                  <SelectItem value={TIMING_REFERENCE.PREVIOUS_TASK}>
                    previous task completes
                  </SelectItem>
                  <SelectItem value={TIMING_REFERENCE.WORKFLOW_END}>
                    workflow ends
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Business Days Handling */}
          <div>
            <div className="text-sm font-medium mb-2">Business Days Handling</div>
            <Select value={businessDayHandling} onValueChange={setBusinessDayHandling}>
              <SelectTrigger className="h-auto">
                <SelectValue>
                  {businessDayHandling === BUSINESS_DAY_OPTIONS.SHIFT_NEXT && (
                    <div className="py-1">
                      <div className="font-medium">Skip Weekends</div>
                      <div className="text-xs text-muted-foreground">
                        If a due date falls on a weekend, it is moved to Monday.
                      </div>
                    </div>
                  )}
                  {businessDayHandling === BUSINESS_DAY_OPTIONS.ALL_DAYS && (
                    <div className="py-1">
                      <div className="font-medium">All Days</div>
                      <div className="text-xs text-muted-foreground">
                        Due dates can fall on any day, including weekends.
                      </div>
                    </div>
                  )}
                </SelectValue>
              </SelectTrigger>
              <SelectContent className="w-[400px]">
                <SelectItem value={BUSINESS_DAY_OPTIONS.SHIFT_NEXT} className="py-3">
                  <div className="flex flex-col gap-1">
                    <div className="font-medium text-primary">Skip Weekends</div>
                    <div className="text-xs text-muted-foreground">
                      If a due date falls on a weekend, it is moved to Monday.
                    </div>
                  </div>
                </SelectItem>
                <SelectItem value={BUSINESS_DAY_OPTIONS.ALL_DAYS} className="py-3">
                  <div className="flex flex-col gap-1">
                    <div className="font-medium">All Days</div>
                    <div className="text-xs text-muted-foreground">
                      Due dates can fall on any day, including weekends.
                    </div>
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <DialogFooter className="flex gap-2 sm:justify-between">
          <Button variant="ghost" onClick={handleRemove} className="text-destructive hover:text-destructive">
            Remove
          </Button>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} className="bg-blue-500 hover:bg-blue-600">
              Save
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
